import { useMemo, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { findKeyPrefixConflict, isDangerousTranslationKey } from "./keys";

export interface CreateWordFormProps {
  existingKeys: Set<string>;
  onCreateWord: (word: { key: string; value: string }) => void;
}

export function CreateWordForm({
  existingKeys,
  onCreateWord,
}: CreateWordFormProps) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");

  const trimmedKey = key.trim();
  const isDuplicate = trimmedKey !== "" && existingKeys.has(trimmedKey);
  const isDangerous =
    trimmedKey !== "" && isDangerousTranslationKey(trimmedKey);

  // Ключи `a` и `a.b` не могут сосуществовать — сервер не соберёт файл
  // перевода (см. findKeyPrefixConflict). Ловим это до Save.
  const prefixConflict = useMemo(() => {
    if (trimmedKey === "" || isDuplicate) return null;
    return findKeyPrefixConflict(existingKeys, trimmedKey);
  }, [existingKeys, trimmedKey, isDuplicate]);

  const error = isDuplicate || isDangerous || Boolean(prefixConflict);
  const valid = trimmedKey !== "" && !error;

  let errorText: string | undefined;

  if (isDuplicate) {
    errorText = "Такой ключ уже существует";
  } else if (isDangerous) {
    errorText = "Сегменты __proto__, constructor и prototype в ключе запрещены";
  } else if (prefixConflict) {
    errorText = `Конфликт с ключом «${prefixConflict}»: один ключ не может быть одновременно строкой и веткой`;
  }

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!valid) return;

    onCreateWord({ key: trimmedKey, value });
    setKey("");
    setValue("");
  };

  return (
    <Card>
      <CardContent>
        <form onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                autoFocus
                fullWidth
                required
                label="Ключ"
                variant="outlined"
                value={key}
                onChange={(event) => setKey(event.target.value)}
                error={error}
                helperText={errorText}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Значение"
                variant="outlined"
                multiline
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!valid}
              >
                Добавить
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
}
